import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Si la variable de entorno está vacía (Prod), usamos el origen actual (para que funcione el proxy /api)
// Si tiene valor (Dev), usamos ese valor (ej: http://localhost:8000)
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = '';

if (!ENV_BACKEND_URL || ENV_BACKEND_URL === '') {
  // Producción: Usar URL relativa
  BACKEND_URL = window.location.origin; 
} else {
  // Desarrollo: Usar valor explícito
  BACKEND_URL = ENV_BACKEND_URL;
}

// Limpiar si trae /api al final
if (BACKEND_URL.endsWith('/api')) {
  BACKEND_URL = BACKEND_URL.slice(0, -4);
}

const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    // Configurar interceptor de axios para incluir token
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('token', access_token);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  // WebSocket logic
  useEffect(() => {
    let ws = null;

    if (user && user.company_id) {
       // Determinar protocolo y host para WS
       const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
       
       let wsHost;
       if (!ENV_BACKEND_URL || ENV_BACKEND_URL === '') {
           // Producción (mismo host)
           wsHost = window.location.host;
       } else {
           // Desarrollo (quitar http:// o https:// del ENV_BACKEND_URL)
           wsHost = ENV_BACKEND_URL.replace(/^https?:\/\//, '');
       }

       const wsUrl = `${protocol}//${wsHost}/ws/${user.company_id}`;
       console.log('Connecting WS to:', wsUrl);
       
       ws = new WebSocket(wsUrl);

       ws.onopen = () => {
         console.log('WS Connected');
       };

       ws.onclose = () => {
         console.log('WS Disconnected');
       };

       setSocket(ws);
    }

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, socket }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};